import { logApiError, logApiPerformance } from '@ms/yammer-diagnostics-state';
import {
  endAndReportPerformanceMeasure,
  getErrorMessage,
  getErrorReason,
  getPerformanceMeasureEntry,
  startPerformanceMeasure,
} from '@ms/yammer-telemetry';

import { apiRequest } from './apiRequest';
import { ApiError } from './errors';
import { getApiMeasureName } from './getApiMeasureName';
import type { ApiRequest, ApiResponse, ManagedApiPerformanceMetadata } from './types';

const getNonEmptyValue = (value: string | null | undefined): string | undefined =>
  value === '' ? undefined : (value ?? undefined);

export const performanceReportedApiRequest = async <TResponse>(
  request: ApiRequest,
  performanceMetadata?: ManagedApiPerformanceMetadata
): Promise<ApiResponse<TResponse>> => {
  const apiName = getNonEmptyValue(getApiMeasureName(request)) ?? 'unknown_api';
  const measure = startPerformanceMeasure('api_fetch', apiName);
  let hasRequestFailed = false;
  let error: unknown;
  let afdRef: string | undefined;
  let traceId: string | undefined;

  try {
    const response = await apiRequest<TResponse>(request);
    afdRef = getNonEmptyValue(response.headers.get('X-MSEdge-Ref'));
    traceId = getNonEmptyValue(response.headers.get('x-trace-id'));

    return response;
  } catch (caughtError) {
    hasRequestFailed = true;
    error = caughtError;
    let requestId: string | undefined;

    /* istanbul ignore else */
    if (error instanceof ApiError) {
      afdRef = getNonEmptyValue(error.afdRef);
      traceId = getNonEmptyValue(error.traceId);
      requestId = getNonEmptyValue(error.requestId);
    }

    logApiError({ name: apiName, traceId, requestId, message: getErrorMessage(error) });
    throw caughtError;
  } finally {
    const requestId = request.headers?.['X-Request-Id'] ?? '';
    endAndReportPerformanceMeasure({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        apiName,
        actionName: performanceMetadata?.actionName,
        actionType: performanceMetadata?.actionType,
        endedWithError: hasRequestFailed,
        message: getErrorMessage(error),
        reason: getErrorReason(error),
        requestId,
        initiator: request.initiator,
        afdRef,
        ...performanceMetadata?.customProperties,
      },
    });
    const { duration } = getPerformanceMeasureEntry(measure.measureKey);
    logApiPerformance({ name: apiName, traceId, duration, requestId });
  }
};
